import { ExternalAssetSource, MediaType } from "../../types";
import { BaseProvider } from "../base/BaseProvider";
import { IAsset, SearchParams } from "../base/types";
import { ImageAsset, VideoAsset } from "./assets";
import { PexelsImageItem, PexelsSearchResponse, PexelsVideoItem } from "./types";

interface PexelsProviderConfig {
  /** Pexels API key from https://www.pexels.com/api/ */
  apiKey: string;
}

/**
 * Pexels Provider
 *
 * Provides read-only access to Pexels' library of free images and videos.
 * https://www.pexels.com/
 *
 * API documentation: https://www.pexels.com/api/documentation/
 * Supports both image search and video search.
 */
export class PexelsProvider extends BaseProvider {
  private static readonly BASE_URL = 'https://api.pexels.com/v1/';
  private static readonly IMAGES_SEARCH_API = `${PexelsProvider.BASE_URL}search`;
  private static readonly VIDEOS_SEARCH_API = `${PexelsProvider.BASE_URL}videos/search`;
  private static readonly IMAGE_API = `${PexelsProvider.BASE_URL}photos/`;
  private static readonly VIDEO_API = `${PexelsProvider.BASE_URL}videos/videos/`;

  constructor(config: PexelsProviderConfig) {
    super({
      id: ExternalAssetSource.Pexels,
      name: 'pexels',
      displayName: 'Pexels',
      description: 'Free images and videos provided by Pexels',
      baseUrl: PexelsProvider.BASE_URL,
      apiKey: config.apiKey,
      capabilities: {
        read: true,
        write: false,
        delete: false,
        supportedTypes: [MediaType.Image, MediaType.Video],
      },
    });
  }

  /**
   * Search for assets on Pexels.
   * Routes to the images API or videos API based on the requested media type.
   * If types contains both Image and Video, returns results from both.
   */
  async search(params: SearchParams): Promise<IAsset[]> {
    const requestedTypes = params.types ?? [MediaType.Image, MediaType.Video];
    const wantsImages = requestedTypes.includes(MediaType.Image);
    const wantsVideos = requestedTypes.includes(MediaType.Video);

    const results: IAsset[] = [];

    if (wantsImages) {
      results.push(...await this.searchImages(params));
    }
    if (wantsVideos) {
      results.push(...await this.searchVideos(params));
    }

    return results;
  }

  private async searchImages(params: SearchParams): Promise<IAsset[]> {
    let url = new URL(PexelsProvider.IMAGES_SEARCH_API);
    if (params.query) {
      url.searchParams.set('query', params.query);
    } else {
      // No query - return curated photos
      // https://api.pexels.com/v1/curated
      url = new URL(`${PexelsProvider.BASE_URL}curated`);
    }
    if (params.category) url.searchParams.set('category', params.category);
    url.searchParams.set('order', params.sort || 'popular');
    url.searchParams.set('page', String(params.page ?? 1));
    url.searchParams.set('per_page', String(params.limit ?? 20));

    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `${this.apiKey}`,
      },
    });
    if (!response.ok) throw new Error(`Pexels image search failed: ${response.statusText}`);
    const data = await response.json() as PexelsSearchResponse<PexelsImageItem>;
    if (!data.photos) return [];
    const photoData = data.photos as PexelsImageItem[];
    return photoData.map(item => new ImageAsset(item));
  }

  private async searchVideos(params: SearchParams): Promise<IAsset[]> {
    let url = new URL(PexelsProvider.VIDEOS_SEARCH_API);
    if (params.query) {
      url.searchParams.set('query', params.query);
    } else {
      // No query - return popular videos
      // https://api.pexels.com/v1/videos/popular
      url = new URL(`${PexelsProvider.BASE_URL}videos/popular`);
    }
    url.searchParams.set('order', params.sort || 'popular');
    url.searchParams.set('page', String(params.page ?? 1));
    url.searchParams.set('per_page', String(params.limit ?? 20));
    if (params.category) url.searchParams.set('category', params.category);

    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `${this.apiKey}`,
      },
    });
    if (!response.ok) throw new Error(`Pexels video search failed: ${response.statusText}`);
    const data = await response.json() as PexelsSearchResponse<PexelsVideoItem>;
    if (!data.videos) return [];
    const videoData = data.videos as PexelsVideoItem[];
    return videoData.map(item => new VideoAsset(item));
  }

  /**
   * Get a specific asset by ID.
   * Pexels uses pageURL as the id — we try images first, then videos.
   */
  async getAssetById(id: string): Promise<IAsset> {
    // The id is the pageURL; extract the numeric Pexels ID from it
    // pageURL looks like: https://api.pexels.com/v1/photos/:id
    // or for videos: https://api.pexels.com/v1/videos/videos/:id

    const match = id.match(/(\d+)\/?$/);
    if (!match) throw new Error(`Invalid Pexels asset ID: ${id}`);

    const numericId = match[1];

    // Try image endpoint first
    try {
      const url = new URL(PexelsProvider.IMAGE_API + numericId);
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `${this.apiKey}`,
        },
      });
      if (!response.ok) throw new Error(`Pexels image lookup failed: ${response.statusText}`);
      const data = await response.json() as PexelsImageItem;
      if (data) return new ImageAsset(data);
    } catch { /* fall through to try videos */}

    // Try video endpoint
    try {
      const url = new URL(PexelsProvider.VIDEO_API + numericId);
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `${this.apiKey}`,
        },
      });
      if (!response.ok) throw new Error(`Pexels video lookup failed: ${response.statusText}`);
      const data = await response.json() as PexelsVideoItem;
      if (data) return new VideoAsset(data);
    } catch { /* fall through */ }
    throw new Error(`Asset with ID ${id} not found in Pexels`);
  }
}
